/**
 * Sync Reports — last completed run per job type, paged ingestion batches,
 * and a per-batch sync_decision journal (WSRADM-0014).
 *
 * Tenant comes from the JWT only. URL: a valid `?job=` opens the journal;
 * invalid or absent `job` shows the list. No `tab=`, no poll, no run-now.
 */
import { useEffect, useRef, useState, type MutableRefObject } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  MenuItem,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  type ChipProps,
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@/api/errors';
import { listIngestionJobs, listJobDecisions, listLastRuns } from '@/api/syncReports/syncReportsApi';
import type {
  IngestionJobView,
  LastRunView,
  SyncDecisionFacts,
  SyncDecisionView,
} from '@/api/syncReports/types';
import { ErrorDetails } from '@/components/ErrorDetails';
import { useCopy } from '@/hooks/useCopy';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useResource } from '@/hooks/useResource';

const JOB_ID_RE = /^[1-9][0-9]*$/;
const HEADER_LOOKUP_SIZE = 100;
const PAGE_SIZES = [20, 50, 100];
const EM_DASH = '—';

const JOB_TYPES = [
  'USER_SYNC',
  'SALES_SYNC',
  'ATTENDANCE_SYNC',
  'PRODUCT_SYNC',
  'SALES_RECONCILE',
  'REPORT_USER_SYNC_SUMMARY',
] as const;

const JOB_STATUSES = ['PENDING', 'CLAIMED', 'RUNNING', 'SUCCEEDED', 'FAILED', 'SKIPPED'] as const;
const SEVERITIES = ['INFO', 'WARN', 'ERROR'] as const;

const KNOWN_COUNTER_KEYS = [
  'fetched',
  'landed',
  'updated',
  'unchanged',
  'failed',
  'ghlUpdates',
  'ghlInserts',
  'ghlFailures',
  'ghlContactsRetrieved',
  'labelRows',
  'categoryRows',
  'detailLines',
  'duplicatesCollapsed',
  'rowsInSheet',
] as const;

const ALWAYS_SHOW_ZERO = new Set(['failed', 'ghlFailures']);
const KNOWN_FACT_KEYS = new Set(['fields', 'tagsAdded', 'tagsRemoved', 'reason']);

type JobCache = MutableRefObject<Map<number, IngestionJobView>>;
type ListRef = MutableRefObject<IngestionJobView[]>;
type CopyFn = ReturnType<typeof useCopy>['t'];

function parseJobId(raw: string | null): number | null {
  if (raw == null || !JOB_ID_RE.test(raw)) return null;
  const n = Number(raw);
  return Number.isSafeInteger(n) ? n : null;
}

function detailsCode(err: unknown): string | undefined {
  if (err instanceof ApiError && typeof err.details?.['code'] === 'string') {
    return err.details['code'];
  }
  return undefined;
}

function jobTypeLabel(jobType: string, t: CopyFn): string {
  return t(`jobType.${jobType}`, jobType);
}

function jobStatusLabel(status: string, t: CopyFn): string {
  return t(`jobStatus.${status}`, status);
}

function severityLabel(severity: string, t: CopyFn): string {
  return t(`severity.${severity}`, severity);
}

function kindLabel(runKind: string | null, t: CopyFn): string {
  if (runKind == null) return EM_DASH;
  return t(`runKind.${runKind}`, runKind);
}

function formatInstant(iso: string | null, language: string): string {
  if (iso == null) return EM_DASH;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return EM_DASH;
  return new Intl.DateTimeFormat(language, { dateStyle: 'medium', timeStyle: 'short' }).format(d);
}

function statusChipColor(status: string): ChipProps['color'] {
  if (status === 'SUCCEEDED') return 'success';
  if (status === 'FAILED') return 'error';
  if (status === 'RUNNING') return 'info';
  return 'default';
}

function severityChipColor(severity: string): ChipProps['color'] {
  if (severity === 'ERROR') return 'error';
  if (severity === 'WARN') return 'warning';
  return 'default';
}

function counterEntries(counters: Record<string, number>): { key: string; value: number }[] {
  const seen = new Set<string>();
  const entries: { key: string; value: number }[] = [];
  for (const key of KNOWN_COUNTER_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(counters, key)) continue;
    const value = counters[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    if (value === 0 && !ALWAYS_SHOW_ZERO.has(key)) continue;
    entries.push({ key, value });
    seen.add(key);
  }
  for (const key of Object.keys(counters)) {
    if (seen.has(key)) continue;
    const value = counters[key];
    if (typeof value !== 'number' || !Number.isFinite(value)) continue;
    entries.push({ key, value });
  }
  return entries;
}

function emptyJournalCopy(jobType: string | undefined, t: CopyFn): string {
  if (jobType === 'USER_SYNC') {
    return t('syncReports.emptyJournalUserSync', 'No per-user changes on this run.');
  }
  if (jobType) {
    return t(
      'syncReports.emptyJournalOtherType',
      'This job type has no per-row journal. Outcome and counts are on the run header.',
    );
  }
  return t('syncReports.emptyJournalUnknown', 'No per-row journal for this run.');
}

function LoadingBlock() {
  return <Skeleton variant="rounded" height={240} role="status" />;
}

function CountersLine({ counters }: { counters: Record<string, number> | null }) {
  const { t } = useCopy();
  if (counters == null) return <>{EM_DASH}</>;
  const entries = counterEntries(counters);
  if (entries.length === 0) return <>{EM_DASH}</>;
  return (
    <Typography component="div" variant="caption" color="text.secondary">
      {entries.map(({ key, value }, index) => (
        <Box
          component="span"
          key={key}
          sx={
            ALWAYS_SHOW_ZERO.has(key) && value !== 0
              ? { color: 'error.main', fontWeight: 600 }
              : undefined
          }
        >
          {index > 0 ? ' · ' : null}
          {t(`syncReports.counters.${key}`, `{{n}} ${key}`, { n: value.toLocaleString() })}
        </Box>
      ))}
    </Typography>
  );
}

function JobStatusChip({ status }: { status: string }) {
  const { t } = useCopy();
  return <Chip size="small" color={statusChipColor(status)} label={jobStatusLabel(status, t)} />;
}

function SeverityChip({ severity }: { severity: string }) {
  const { t } = useCopy();
  return (
    <Chip size="small" color={severityChipColor(severity)} label={severityLabel(severity, t)} />
  );
}

export function SyncReportsPage() {
  const { t } = useCopy();
  useDocumentTitle(t('page.syncReports.title', 'Sync Reports'));
  const [searchParams, setSearchParams] = useSearchParams();
  const jobId = parseJobId(searchParams.get('job'));
  const jobCache = useRef(new Map<number, IngestionJobView>());
  const listContentRef = useRef<IngestionJobView[]>([]);

  if (jobId !== null) {
    return (
      <JournalView
        key={jobId}
        jobId={jobId}
        jobCache={jobCache}
        listContentRef={listContentRef}
        onBack={() => setSearchParams({})}
      />
    );
  }

  return (
    <JobsListView
      jobCache={jobCache}
      listContentRef={listContentRef}
      onOpenJob={(job) => {
        jobCache.current.set(job.id, job);
        setSearchParams({ job: String(job.id) });
      }}
      onOpenLastRun={(run) => {
        setSearchParams({ job: String(run.id) });
      }}
    />
  );
}

function JobsListView({
  jobCache,
  listContentRef,
  onOpenJob,
  onOpenLastRun,
}: {
  jobCache: JobCache;
  listContentRef: ListRef;
  onOpenJob: (job: IngestionJobView) => void;
  onOpenLastRun: (run: LastRunView) => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  const jobs = useResource(
    () =>
      listIngestionJobs({
        ...(typeFilter ? { type: typeFilter } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
        page,
        size,
      }),
    [typeFilter, statusFilter, page, size],
  );

  useEffect(() => {
    if (jobs.status !== 'ready' || jobs.data == null) return;
    listContentRef.current = jobs.data.content;
    for (const job of jobs.data.content) jobCache.current.set(job.id, job);
  }, [jobs.status, jobs.data, jobCache, listContentRef]);

  const jobsCode = detailsCode(jobs.error);
  const unknownTenant = jobs.status === 'error' && jobsCode === 'UNKNOWN_TENANT';

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ mb: 2.5 }}>
        {t('page.syncReports.title', 'Sync Reports')}
      </Typography>

      <LastRunsStrip onOpenLastRun={onOpenLastRun} />

      {unknownTenant ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('syncReports.unknownTenant', 'This session has no tenant. Sign in again.')}
        </Alert>
      ) : null}

      {jobs.status === 'error' && !unknownTenant ? (
        <Alert
          severity="error"
          sx={{ mt: 2, mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={jobs.refetch}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('syncReports.loadError', 'Could not load sync history.')}
          <ErrorDetails
            intent="read"
            error={jobs.error}
            headline={t('syncReports.loadError', 'Could not load sync history.')}
          />
        </Alert>
      ) : null}

      {!unknownTenant ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{ mb: 2, mt: 2, alignItems: { xs: 'stretch', sm: 'center' } }}
        >
          <TextField
            select
            label={t('syncReports.filters.typeLabel', 'Job type')}
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 220 }}
          >
            <MenuItem value="">{t('syncReports.filters.allTypes', 'All types')}</MenuItem>
            {JOB_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {jobTypeLabel(type, t)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('syncReports.filters.statusLabel', 'Status')}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('syncReports.filters.allStatuses', 'All statuses')}</MenuItem>
            {JOB_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {jobStatusLabel(status, t)}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      ) : null}

      {jobs.status === 'loading' ? <LoadingBlock /> : null}

      {jobs.status === 'ready' && jobs.data != null && !unknownTenant ? (
        jobs.data.content.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {t('syncReports.emptyJobs', 'No sync runs in the last 14 days.')}
          </Typography>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table aria-label={t('page.syncReports.title', 'Sync Reports')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('syncReports.col.id', 'Id')}</TableCell>
                    <TableCell>{t('syncReports.col.job', 'Job')}</TableCell>
                    <TableCell>{t('syncReports.col.status', 'Status')}</TableCell>
                    <TableCell>{t('syncReports.col.kind', 'Kind')}</TableCell>
                    <TableCell>{t('syncReports.col.finished', 'Finished')}</TableCell>
                    <TableCell>{t('syncReports.col.records', 'Records')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.data.content.map((job) => (
                    <TableRow key={job.id}>
                      <TableCell>{job.id}</TableCell>
                      <TableCell>
                        <Button variant="text" size="small" onClick={() => onOpenJob(job)}>
                          {jobTypeLabel(job.jobType, t)}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <JobStatusChip status={job.status} />
                      </TableCell>
                      <TableCell>{kindLabel(job.runKind, t)}</TableCell>
                      <TableCell>{formatInstant(job.finishedAt, i18n.language)}</TableCell>
                      <TableCell>
                        <CountersLine counters={job.runCounters} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={jobs.data.totalElements}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={size}
              onRowsPerPageChange={(e) => {
                setSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={PAGE_SIZES}
            />
          </>
        )
      ) : null}
    </Box>
  );
}

function LastRunsStrip({ onOpenLastRun }: { onOpenLastRun: (run: LastRunView) => void }) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const lastRuns = useResource(() => listLastRuns(), []);
  const code = detailsCode(lastRuns.error);
  const unknownTenant = lastRuns.status === 'error' && code === 'UNKNOWN_TENANT';

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h3" component="h2" sx={{ mb: 1.5 }}>
        {t('syncReports.lastRuns', 'Last completed')}
      </Typography>

      {unknownTenant ? (
        <Alert severity="info">
          {t('syncReports.unknownTenant', 'This session has no tenant. Sign in again.')}
        </Alert>
      ) : null}

      {lastRuns.status === 'error' && !unknownTenant ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={lastRuns.refetch}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('syncReports.loadError', 'Could not load sync history.')}
          <ErrorDetails
            intent="read"
            error={lastRuns.error}
            headline={t('syncReports.loadError', 'Could not load sync history.')}
          />
        </Alert>
      ) : null}

      {lastRuns.status === 'loading' ? <Skeleton variant="rounded" height={120} role="status" /> : null}

      {lastRuns.status === 'ready' && lastRuns.data != null ? (
        lastRuns.data.lastRuns.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('syncReports.emptyLastRuns', 'No completed runs yet.')}
          </Typography>
        ) : (
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table aria-label={t('syncReports.lastRuns', 'Last completed')}>
              <TableHead>
                <TableRow>
                  <TableCell>{t('syncReports.col.job', 'Job')}</TableCell>
                  <TableCell>{t('syncReports.col.status', 'Status')}</TableCell>
                  <TableCell>{t('syncReports.col.kind', 'Kind')}</TableCell>
                  <TableCell>{t('syncReports.col.finished', 'Finished')}</TableCell>
                  <TableCell>{t('syncReports.col.records', 'Records')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {lastRuns.data.lastRuns.map((run) => (
                  <TableRow key={`${run.jobType}-${run.id}`}>
                    <TableCell>
                      <Button variant="text" size="small" onClick={() => onOpenLastRun(run)}>
                        {jobTypeLabel(run.jobType, t)}
                      </Button>
                    </TableCell>
                    <TableCell>
                      <JobStatusChip status={run.status} />
                    </TableCell>
                    <TableCell>{kindLabel(run.runKind, t)}</TableCell>
                    <TableCell>{formatInstant(run.finishedAt, i18n.language)}</TableCell>
                    <TableCell>
                      <CountersLine counters={run.counters} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      ) : null}
    </Box>
  );
}

function JournalView({
  jobId,
  jobCache,
  listContentRef,
  onBack,
}: {
  jobId: number;
  jobCache: JobCache;
  listContentRef: ListRef;
  onBack: () => void;
}) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const [header, setHeader] = useState<IngestionJobView | null>(null);
  const [headerPending, setHeaderPending] = useState(true);
  const [severityFilter, setSeverityFilter] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(20);

  useEffect(() => {
    const cached =
      jobCache.current.get(jobId) ?? listContentRef.current.find((job) => job.id === jobId);
    if (cached) {
      jobCache.current.set(jobId, cached);
      setHeader(cached);
      setHeaderPending(false);
      return;
    }
    let cancelled = false;
    listIngestionJobs({ page: 0, size: HEADER_LOOKUP_SIZE })
      .then((res) => {
        if (cancelled) return;
        const found = res.content.find((job) => job.id === jobId) ?? null;
        if (found) jobCache.current.set(jobId, found);
        setHeader(found);
        setHeaderPending(false);
      })
      .catch(() => {
        if (!cancelled) {
          setHeader(null);
          setHeaderPending(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [jobCache, jobId, listContentRef]);

  const decisions = useResource(
    () =>
      listJobDecisions(jobId, {
        ...(severityFilter ? { severity: severityFilter } : {}),
        page,
        size,
      }),
    [jobId, severityFilter, page, size],
  );

  const code = detailsCode(decisions.error);
  const unknownTenant = decisions.status === 'error' && code === 'UNKNOWN_TENANT';
  const jobGone = decisions.status === 'error' && code === 'JOB_NOT_FOUND';
  const resolvedType = headerPending ? undefined : (header?.jobType ?? decisions.data?.content[0]?.jobType);
  const journalPending = decisions.status === 'loading' || headerPending;
  const backControl = (
    <Button variant="text" onClick={onBack}>
      {t('syncReports.backToList', 'Back to list')}
    </Button>
  );

  if (unknownTenant) {
    return (
      <Box>
        <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
          {t('page.syncReports.title', 'Sync Reports')}
        </Typography>
        {backControl}
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('syncReports.unknownTenant', 'This session has no tenant. Sign in again.')}
        </Alert>
      </Box>
    );
  }

  if (jobGone) {
    return (
      <Box>
        <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
          {t('page.syncReports.title', 'Sync Reports')}
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          {t('syncReports.jobNotFound', 'This run is no longer retained.')}
        </Alert>
        {backControl}
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ mb: 2 }}>
        {t('page.syncReports.title', 'Sync Reports')}
      </Typography>
      {backControl}
      <Typography variant="h2" component="h2" sx={{ mt: 1 }} gutterBottom>
        {t('syncReports.jobTitle', 'Job {{id}}', { id: jobId })}
      </Typography>

      {!headerPending ? (
        header ? (
          <Stack spacing={1} sx={{ mb: 3 }}>
            <Typography variant="body1">{jobTypeLabel(header.jobType, t)}</Typography>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
              <JobStatusChip status={header.status} />
              <Typography variant="body2">{kindLabel(header.runKind, t)}</Typography>
              <Typography variant="body2" color="text.secondary">
                {formatInstant(header.finishedAt, i18n.language)}
              </Typography>
            </Stack>
            <CountersLine counters={header.runCounters} />
            {header.errorDetail ? (
              <Box>
                <Typography variant="body2">{header.errorDetail}</Typography>
                {header.errorCode ? (
                  <Typography variant="caption" color="text.secondary">
                    {header.errorCode}
                  </Typography>
                ) : null}
              </Box>
            ) : null}
          </Stack>
        ) : (
          <Stack spacing={1} sx={{ mb: 3 }}>
            {resolvedType ? (
              <Typography variant="body1">{jobTypeLabel(resolvedType, t)}</Typography>
            ) : (
              <Typography variant="body2">{EM_DASH}</Typography>
            )}
          </Stack>
        )
      ) : null}

      <Typography variant="h3" component="h3" sx={{ mb: 2 }}>
        {t('syncReports.journalTitle', 'Journal')}
      </Typography>

      {decisions.status === 'error' ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={decisions.refetch}>
              {t('common.retry', 'Retry')}
            </Button>
          }
        >
          {t('syncReports.loadError', 'Could not load sync history.')}
          <ErrorDetails
            intent="read"
            error={decisions.error}
            headline={t('syncReports.loadError', 'Could not load sync history.')}
          />
        </Alert>
      ) : null}

      <TextField
        select
        label={t('syncReports.filters.severityLabel', 'Severity')}
        value={severityFilter}
        onChange={(e) => {
          setSeverityFilter(e.target.value);
          setPage(0);
        }}
        sx={{ minWidth: 180, mb: 2 }}
      >
        <MenuItem value="">{t('syncReports.filters.allSeverities', 'All severities')}</MenuItem>
        {SEVERITIES.map((sev) => (
          <MenuItem key={sev} value={sev}>
            {severityLabel(sev, t)}
          </MenuItem>
        ))}
      </TextField>

      {journalPending ? <LoadingBlock /> : null}

      {!journalPending && decisions.status === 'ready' && decisions.data != null ? (
        decisions.data.content.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {emptyJournalCopy(resolvedType, t)}
          </Typography>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table aria-label={t('syncReports.journalTitle', 'Journal')}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('syncReports.col.when', 'When')}</TableCell>
                    <TableCell>{t('syncReports.col.severity', 'Sev')}</TableCell>
                    <TableCell>{t('syncReports.col.action', 'Action')}</TableCell>
                    <TableCell>{t('syncReports.col.entity', 'Entity')}</TableCell>
                    <TableCell>{t('syncReports.col.summary', 'Summary')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {decisions.data.content.map((row) => (
                    <DecisionRow key={row.id} row={row} language={i18n.language} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={decisions.data.totalElements}
              page={page}
              onPageChange={(_, next) => setPage(next)}
              rowsPerPage={size}
              onRowsPerPageChange={(e) => {
                setSize(Number(e.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={PAGE_SIZES}
            />
          </>
        )
      ) : null}
    </Box>
  );
}

function DecisionRow({ row, language }: { row: SyncDecisionView; language: string }) {
  const { t } = useCopy();
  const [expanded, setExpanded] = useState(false);
  const facts = row.facts;
  const hasFacts = facts != null && typeof facts === 'object';

  return (
    <TableRow>
      <TableCell>{formatInstant(row.occurredAt, language)}</TableCell>
      <TableCell>
        <SeverityChip severity={row.severity} />
      </TableCell>
      <TableCell>{t(`syncReports.action.${row.action}`, row.action)}</TableCell>
      <TableCell>
        {t(`syncReports.entity.${row.entityKind}`, row.entityKind)} {row.entityKey}
      </TableCell>
      <TableCell>
        <Typography variant="body2">{row.summary}</Typography>
        {hasFacts ? (
          <Box sx={{ mt: 0.5 }}>
            <Button
              size="small"
              variant="text"
              aria-expanded={expanded}
              onClick={() => setExpanded((open) => !open)}
            >
              {expanded
                ? t('syncReports.facts.hide', 'Hide details')
                : t('syncReports.facts.show', 'Show details')}
            </Button>
            {expanded ? <FactsDetail facts={facts} /> : null}
          </Box>
        ) : null}
      </TableCell>
    </TableRow>
  );
}

function FactsDetail({ facts }: { facts: SyncDecisionFacts | Record<string, unknown> }) {
  const typed = facts as {
    fields?: Array<{ name: string; from: string; to: string }>;
    tagsAdded?: string[];
    tagsRemoved?: string[];
    reason?: string;
  };

  return (
    <Box sx={{ mt: 1 }}>
      {typed.fields?.map((field) => (
        <Typography key={field.name} variant="body2">
          {field.name}: {field.from} → {field.to}
        </Typography>
      ))}
      {typed.tagsAdded && typed.tagsAdded.length > 0 ? (
        <Stack direction="row" spacing={0.5} sx={{ my: 0.5, flexWrap: 'wrap' }}>
          {typed.tagsAdded.map((tag) => (
            <Chip key={`add-${tag}`} label={tag} size="small" variant="outlined" />
          ))}
        </Stack>
      ) : null}
      {typed.tagsRemoved && typed.tagsRemoved.length > 0 ? (
        <Stack direction="row" spacing={0.5} sx={{ my: 0.5, flexWrap: 'wrap' }}>
          {typed.tagsRemoved.map((tag) => (
            <Chip key={`rm-${tag}`} label={tag} size="small" variant="outlined" />
          ))}
        </Stack>
      ) : null}
      {typed.reason ? (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {typed.reason}
        </Typography>
      ) : null}
      {Object.keys(facts)
        .filter((key) => !KNOWN_FACT_KEYS.has(key))
        .map((key) => {
          const value = (facts as Record<string, unknown>)[key];
          if (
            value == null ||
            (typeof value !== 'string' && typeof value !== 'number' && typeof value !== 'boolean')
          ) {
            return null;
          }
          return (
            <Typography key={key} variant="body2">
              {key}: {String(value)}
            </Typography>
          );
        })}
    </Box>
  );
}
